<template>
    <div class="autocomplete">
        <input
            v-model="search"
            @input="onChange"
            @keydown.down="onArrowDown"
            @keydown.up="onArrowUp"
            @keydown.enter="onEnter"
            type="text"
            autocomplete="off"
            :class="inputClass"
            :placeholder="placeholder"
            :disabled="disableInput"
            :maxlength="maxlength"
        />
        <ul
            id="autocomplete-results"
            v-show="isOpen"
            class="autocomplete-results"
        >
            <slot name="results">
                <li
                    class="loading"
                    v-if="isLoading"
                >
                    Loading results...
                </li>
                <li
                    v-else
                    v-for="(result, i) in results"
                    :key="i"
                    @click="setResult(result)"
                    class="autocomplete-result"
                    :class="{ 'is-active': i === arrowCounter }"
                    v-html="formatDisplay(result)"
                >
                </li>
            </slot>

        </ul>
    </div>
</template>

<script>
export default {
    name: "SearchAutocomplete",
    props: {
        items: {
            type: Array,
            required: false,
            default: () => [],
        },
        isAsync: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * Data source for the results
         *   `String` is a url, typed input will be appended
         *   `Function` received typed input, and must return a string; to be used as a url
         *   `Array` and `Object` (see `results-property`) are used directly
         */
        source: {
            type: [String, Function, Array, Object],
            required: true
        },
        /**
         * http method
         */
        method: {
            type: String,
            default: 'get'
        },
        /**
         * Input placeholder
         */
        placeholder: {
            default: 'Search'
        },
        /**
         * Preset starting value
         */
        initialValue: {
            type: [String, Number]
        },
        /**
         * Preset starting display value
         */
        initialDisplay: {
            type: String
        },
        /**
         * CSS class for the surrounding input div
         */
        inputClass: {
            type: [String, Object]
        },
        /**
         * To disable the input
         */
        disableInput: {
            type: Boolean,
            default: false
        },
        /**
         * name property of the input holding the selected value
         */
        name: {
            type: String
        },
        /**
         * api - property of results array
         */
        resultsProperty: {
            type: String
        },
        /**
         * Results property used as the value
         */
        resultsValue: {
            type: String,
            default: 'id'
        },
        /**
         * Results property used as the display
         */
        resultsDisplay: {
            type: [String, Function],
            default: 'name'
        },
        /**
         * Callback to format the server data
         */
        resultsFormatter: {
            type: Function
        },
        /**
         * Whether to show the no results message
         */
        showNoResults: {
            type: Boolean,
            default: true
        },
        /**
         * Additional request headers
         */
        requestHeaders: {
            type: Object
        },
        /**
         * Credentials: same-origin, include, *omit
         */
        credentials: {
            type: String
        },
        /**
         * Optional clear button icon class
         */
        clearButtonIcon: {
            type: String
        },
        /**
         * Optional max input length
         */
        maxlength: {
            type: Number
        }
    },
    mounted() {
        this.value = this.initialValue
        this.display = this.initialDisplay
        this.selectedDisplay = this.initialDisplay
        document.addEventListener('click', this.handleClickOutside);
    },
    destroyed() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    data() {
        return {
            search: '',
            value: null,
            display: null,
            results: [],
            selectedIndex: null,
            isOpen: false,
            arrowCounter: -1,
            loading: false,
            isFocussed: false,
            error: null,
            selectedId: null,
            selectedDisplay: null,
            eventListener: false
        };
    },
    computed: {
        showResults () {
            return Array.isArray(this.results) || this.hasError
        },
        noResults () {
            return Array.isArray(this.results) && this.results.length === 0
        },
        noResultMessage () {
            return this.noResults &&
                !this.isLoading &&
                this.isFocussed &&
                !this.hasError &&
                this.showNoResults
        },
        isEmpty () {
            return !this.display
        },
        isLoading () {
            return this.loading === true
        },
        hasError () {
            return this.error !== null
        },
        listStyle () {
            if (this.isLoading) {
                return {
                    color: '#ccc'
                }
            }
        },
    },
    methods: {
        setResult(result) {
            this.search = result;
            this.isOpen = false;
        },
        filterResults() {
            this.results = this.items.filter((item) => {
                return item.toLowerCase().indexOf(this.search.toLowerCase()) > -1;
            });
        },
        onChange() {
            this.$emit('input', this.search);

            if (this.isAsync) {
                this.isLoading = true;
            } else {
                this.filterResults();
                this.isOpen = true;
            }
        },
        handleClickOutside(event) {
            if (!this.$el.contains(event.target)) {
                this.isOpen = false;
                this.arrowCounter = -1;
            }
        },
        onArrowDown() {
            if (this.arrowCounter < this.results.length) {
                this.arrowCounter = this.arrowCounter + 1;
            }
        },
        onArrowUp() {
            if (this.arrowCounter > 0) {
                this.arrowCounter = this.arrowCounter - 1;
            }
        },
        onEnter() {
            this.search = this.results[this.arrowCounter];
            this.isOpen = false;
            this.arrowCounter = -1;
        },
        /**
         * @param  {Object} obj
         * @return {String}
         */
        formatDisplay (obj) {
            switch (typeof this.resultsDisplay) {
                case 'function':
                    return this.resultsDisplay(obj)
                case 'string':
                    if (!obj[this.resultsDisplay]) {
                        throw new Error(`"${this.resultsDisplay}" property expected on result but is not defined.`)
                    }
                    return obj[this.resultsDisplay]
                default:
                    throw new TypeError()
            }
        },
    },
    watch: {
        items: function (value, oldValue) {
            if (value.length !== oldValue.length) {
                this.results = value;
                this.isLoading = false;
            }
        }
    },
}
</script>

<style scoped>
.autocomplete {
    position: relative;
}

.autocomplete-results {
    padding: 0;
    margin: 0;
    border: 1px solid #eeeeee;
    height: 120px;
    min-height: 1em;
    max-height: 6em;
    overflow: auto;
}

.autocomplete-result {
    list-style: none;
    text-align: left;
    padding: 4px 2px;
    cursor: pointer;
}

.autocomplete-result.is-active,
.autocomplete-result:hover {
    background-color: #4AAE9B;
    color: white;
}
</style>
